import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import { CalendarsPickerBody } from "./CalendarsPickerBody.tsx";
import { ConnectorPickerBlock } from "./ConnectorPickerBlock.tsx";
import { LocationsPickerBody } from "./LocationsPickerBody.tsx";
import { ProtectedMeetingsPickerBody } from "./ProtectedMeetingsPickerBody.tsx";
import { RoutinesPickerBody } from "./RoutinesPickerBody.tsx";
import { SlackChannelsPickerBody } from "./SlackChannelsPickerBody.tsx";

type PickerComponentData = Extract<ArtifactComponent, { type: "picker" }>;

/**
 * One card the user picks things on, dispatched by what they're picking.
 *
 * Each kind's body is bespoke and written twice — here and in native — because
 * these are the visual surfaces the assistant brings forward and they should
 * look like it. What this indirection buys is that everything *around* the body
 * is shared: one component type, one schema, one action route, one registry
 * entry per platform. Adding a kind is a `case` here and its twin in native.
 *
 * Bodies read live state themselves rather than taking a list from the
 * component — see `ConnectorPickerBlock`. A card that describes a world which
 * has already moved on is worse than one that takes a moment to load.
 */
export const PickerBlock = ({ component }: { component: PickerComponentData }) => {
  const { target } = component;
  switch (target.kind) {
    case "connectors":
      return (
        <ConnectorPickerBlock
          scopeIds={target.scopeIds}
          pinnedIds={target.pinnedIds}
          preferredIds={target.preferredIds}
        />
      );
    case "calendars":
      return <CalendarsPickerBody />;
    case "protectedMeetings":
      return <ProtectedMeetingsPickerBody />;
    case "locations":
      return <LocationsPickerBody />;
    case "routines":
      return <RoutinesPickerBody />;
    case "slackChannels":
      return <SlackChannelsPickerBody teamId={target.teamId} />;
    default:
      // A card raised by a newer server. Say so rather than rendering nothing:
      // a picker pauses the thread waiting to be acted on, so an unhandled kind
      // becomes a turn stuck behind a blank space, and the symptom points
      // nowhere near the cause.
      return (
        <p className="text-xs text-muted-foreground">This card needs a newer version of the app.</p>
      );
  }
};
